import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH
} from "lexical";
import { useEffect } from "react";
import { $createCustomLinkNode } from "../nodes/CustomLinkNode";

export function CustomLinkPlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        TOGGLE_LINK_COMMAND,
        (payload) => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) {
            return false;
          }

          // Handle both string URLs and payload objects
          const url =
            typeof payload === "string" ? payload : (payload?.url ?? null);

          if (url === null) {
            // Remove link - unwrap the link node
            const nodes = selection.extract();
            nodes.forEach((node) => {
              const parent = node.getParent();
              if ($isLinkNode(parent)) {
                const children = parent.getChildren();
                for (const child of children) {
                  parent.insertBefore(child);
                }
                parent.remove();
              }
            });
          } else {
            // Add or update link
            const nodes = selection.extract();

            // Check if any node is already inside a link
            const linkNode = nodes.find((node) =>
              $isLinkNode(node.getParent())
            );

            if (linkNode) {
              // Update existing link URL
              const parent = linkNode.getParent();
              if ($isLinkNode(parent)) {
                parent.setURL(url);
              }
            } else {
              // Create new custom link node
              const customLink = $createCustomLinkNode(url);

              // For block-level elements, we need to handle them differently
              if ($isElementNode(nodes[0]) && !nodes[0].isInline()) {
                // Insert link inside the block
                nodes[0].append(customLink);
              } else {
                // Replace selection with link containing the selected nodes
                if (nodes.length > 0) {
                  const firstNode = nodes[0];
                  firstNode.insertBefore(customLink);

                  for (const node of nodes) {
                    customLink.append(node);
                  }
                }
              }
            }
          }

          return true;
        },
        COMMAND_PRIORITY_HIGH
      )
    );
  }, [editor]);

  return null;
}
